import { useContext, useState } from 'react';
import { AuthContext, GroupContext } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Key, Eye, EyeOff, Building2, Users, Check, Crown, UserCog, Eye as ViewIcon, Smartphone, QrCode, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role badge colors and icons
const ROLE_CONFIG = {
  admin: { color: 'bg-blue-100 text-blue-800', icon: Crown, label: 'Admin' },
  member: { color: 'bg-green-100 text-green-800', icon: Users, label: 'Member' },
  viewer: { color: 'bg-gray-100 text-gray-800', icon: ViewIcon, label: 'Viewer' },
  external: { color: 'bg-purple-100 text-purple-800', icon: UserCog, label: 'External' }
};

export default function Profile() {
  const { user } = useContext(AuthContext);
  const { groups, activeGroup, switchGroup } = useContext(GroupContext);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changing, setChanging] = useState(false);

  const isPasswordValid = () => {
    const pwd = passwordForm.newPassword;
    return (
      pwd.length >= 10 &&
      /[A-Z]/.test(pwd) &&
      /[a-z]/.test(pwd) &&
      /[0-9]/.test(pwd) &&
      /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pwd) &&
      pwd === passwordForm.confirmPassword
    );
  };

  const handleChangePassword = async () => {
    if (!isPasswordValid()) {
      toast.error('Please ensure all password requirements are met');
      return;
    }

    setChanging(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChanging(false);
    }
  };

  const handleSwitchGroup = async (groupId) => {
    if (groupId === activeGroup?.id) return;
    
    const success = await switchGroup(groupId);
    if (success) {
      const newGroup = groups.find(g => g.id === groupId);
      toast.success(`Switched to ${newGroup?.name || 'group'}`);
      window.location.reload();
    } else {
      toast.error('Failed to switch group');
    }
  };

  const pwd = passwordForm.newPassword;

  return (
    <div className="space-y-6 fade-in" data-testid="profile-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Profile</h1>
        <p className="text-gray-600">Your account information</p>
      </div>

      <Card className="card-hover max-w-2xl">
        <CardHeader>
          <CardTitle>Account Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-6 mb-6">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-blue-100 text-blue-600 text-2xl">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{user?.name}</h2>
              <p className="text-gray-600">
                {user?.role === 'super_admin' || user?.is_platform_admin 
                  ? 'Platform Administrator' 
                  : activeGroup 
                    ? `${activeGroup.name} Member` 
                    : 'Group Member'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Username</label>
              <p className="text-gray-900 mt-1">{user?.username || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Email</label>
              <p className="text-gray-900 mt-1">{user?.email}</p>
            </div>
            {user?.phone && (
              <div>
                <label className="text-sm font-medium text-gray-600">Phone</label>
                <p className="text-gray-900 mt-1">{user.phone}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-600">Account Type</label>
              <p className="text-gray-900 mt-1 capitalize">
                {user?.role === 'super_admin' ? 'Super Admin' 
                  : user?.role === 'admin' ? 'Administrator'
                  : user?.role === 'external_org' ? `External Partner (${user?.organization || ''})`
                  : 'Group Member'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Groups Card */}
      <Card className="card-hover max-w-2xl" data-testid="my-groups-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            My Groups
          </CardTitle>
          <CardDescription>
            Groups you are a member of and your roles within each
          </CardDescription>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <p className="text-gray-500 text-sm">You are not a member of any groups yet.</p>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => {
                const roleConfig = ROLE_CONFIG[group.my_role] || ROLE_CONFIG.member;
                const RoleIcon = roleConfig.icon;
                const isActive = activeGroup?.id === group.id;
                const groupColor = group.settings?.color || '#3B82F6';
                
                return (
                  <div 
                    key={group.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                      isActive 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`group-card-${group.prefix}`}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: groupColor }}
                      >
                        {group.short_name?.charAt(0) || group.name?.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{group.name}</span>
                          {group.is_default && (
                            <Badge variant="outline" className="text-xs">Default</Badge>
                          )}
                          {isActive && (
                            <Badge className="text-xs bg-blue-500">Active</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{group.prefix} • {group.description?.substring(0, 50) || 'No description'}{group.description?.length > 50 ? '...' : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${roleConfig.color} flex items-center gap-1`}>
                        <RoleIcon className="w-3 h-3" />
                        {roleConfig.label}
                        {group.is_platform_admin_access && ' (Platform)'}
                      </Badge>
                      {!isActive && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleSwitchGroup(group.id)}
                          data-testid={`switch-to-${group.prefix}`}
                        >
                          Switch
                        </Button>
                      )}
                      {isActive && (
                        <Check className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card className="card-hover max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Security
          </CardTitle>
          <CardDescription>
            Manage your password and account security
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="change-password-btn">
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Change Password</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPwd ? "text" : "password"}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showCurrentPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPwd ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPwd(!showNewPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPwd ? "text" : "password"}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showConfirmPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Password requirements */}
                <div className="text-xs space-y-1 bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-700 mb-2">Password requirements:</p>
                  <p className={pwd.length >= 10 ? 'text-green-600' : 'text-gray-500'}>
                    {pwd.length >= 10 ? '✓' : '○'} At least 10 characters
                  </p>
                  <p className={/[A-Z]/.test(pwd) ? 'text-green-600' : 'text-gray-500'}>
                    {/[A-Z]/.test(pwd) ? '✓' : '○'} At least 1 uppercase letter (A-Z)
                  </p>
                  <p className={/[a-z]/.test(pwd) ? 'text-green-600' : 'text-gray-500'}>
                    {/[a-z]/.test(pwd) ? '✓' : '○'} At least 1 lowercase letter (a-z)
                  </p>
                  <p className={/[0-9]/.test(pwd) ? 'text-green-600' : 'text-gray-500'}>
                    {/[0-9]/.test(pwd) ? '✓' : '○'} At least 1 number (0-9)
                  </p>
                  <p className={/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pwd) ? 'text-green-600' : 'text-gray-500'}>
                    {/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pwd) ? '✓' : '○'} At least 1 special character (!@#$% etc.)
                  </p>
                  <p className={pwd && passwordForm.confirmPassword && pwd === passwordForm.confirmPassword ? 'text-green-600' : 'text-gray-500'}>
                    {pwd && passwordForm.confirmPassword && pwd === passwordForm.confirmPassword ? '✓' : '○'} Passwords match
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowChangePassword(false)}>Cancel</Button>
                <Button 
                  onClick={handleChangePassword} 
                  disabled={changing || !passwordForm.currentPassword || !isPasswordValid()}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  {changing ? 'Changing...' : 'Change Password'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Mobile App Card */}
      <Card className="card-hover max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Mobile App
          </CardTitle>
          <CardDescription>
            Access HLRA Actions on your phone for site visits and on-the-go updates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">Features</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• View and update your assigned actions</li>
              <li>• Create new actions (if permitted)</li>
              <li>• Works offline - sync when back online</li>
              <li>• Switch between groups</li>
              <li>• Priority dashboard showing overdue & due soon</li>
            </ul>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              Install on Your Phone
            </h4>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Scan this QR code with your phone camera, or click the button below:
                </p>
                
                {/* QR Code - generated using a simple SVG pattern */}
                <div className="w-32 h-32 bg-white border-2 border-gray-200 rounded-lg p-2 mb-3">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(window.location.origin + '/mobile')}`}
                    alt="Mobile App QR Code"
                    className="w-full h-full"
                  />
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open('/mobile', '_blank')}
                  data-testid="open-mobile-app-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Mobile Version
                </Button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="font-medium text-gray-700 mb-2 text-sm">Installation Steps</h5>
                <ol className="text-xs text-gray-600 space-y-2">
                  <li>
                    <strong>iPhone (Safari):</strong>
                    <br />Tap Share → "Add to Home Screen"
                  </li>
                  <li>
                    <strong>Android (Chrome):</strong>
                    <br />Tap menu (⋮) → "Install app" or "Add to Home Screen"
                  </li>
                  <li>
                    <strong>Then:</strong>
                    <br />Look for the HLRA Actions icon on your home screen
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            The mobile app is a Progressive Web App (PWA). It works on any modern smartphone browser and can be installed without an app store.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
